import React, { useState, useCallback } from "react";
import { Edit } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { FormField } from "./ui/form_field";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "./ui/Modal";
import { useToast } from "./ui/toast";
import { getFormIconClassName } from "@/lib/utils";

interface RenameWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceSlug: string;
  workspaceName: string;
  onRenameSuccess?: (newName: string) => void;
}

interface RenameFormData {
  name: string;
}

export default function RenameWorkspaceModal({
  isOpen,
  onClose,
  workspaceSlug,
  workspaceName,
  onRenameSuccess,
}: RenameWorkspaceModalProps) {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<RenameFormData>({
    name: workspaceName,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
      setFormData({ name: workspaceName });
      setErrors({});
    }
  }, [isSubmitting, onClose, workspaceName]);

  const handleInputChange = (value: string) => {
    setFormData({ name: value });
    // Clear error when user starts typing
    if (errors.name) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.name;
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    const trimmedName = formData.name.trim();

    try {
      const response = await fetch(`/workspaces/${workspaceSlug}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
        body: JSON.stringify({
          organization: {
            name: trimmedName,
          },
        }),
      });

      const result = await response.json();

      if (result.success) {
        showToast("success", result.message || "Workspace renamed successfully!");

        // Call the success callback with the new name
        if (onRenameSuccess) {
          onRenameSuccess(trimmedName);
        }

        handleClose();
      } else {
        const formattedErrors: Record<string, string> = {};

        if (Array.isArray(result.errors)) {
          // New format from ErrorSerializer: ["Name is too long..."]
          result.errors.forEach((error: string) => {
            if (error.toLowerCase().startsWith('name ')) {
              formattedErrors.name = error;
            } else {
              // Fallback: assume it's a name validation error since that's our only field
              formattedErrors.name = error;
            }
          });
        } else {
          // Legacy format: { "name": ["Error"] }
          Object.entries(result.errors || {}).forEach(([key, value]) => {
            formattedErrors[key] = Array.isArray(value) ? value[0] : value;
          });
        }

        setErrors(formattedErrors);
      }
    } catch (error) {
      console.error("Error renaming workspace:", error);
      showToast('error', 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form data when workspace name changes (when modal opens with different workspace)
  React.useEffect(() => {
    setFormData({ name: workspaceName });
    setErrors({});
  }, [workspaceName]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      canClose={!isSubmitting}
      size="md"
    >
      <ModalHeader
        icon={<Edit className="w-5 h-5 text-primary" />}
        title="Rename workspace"
        subtitle="Choose a new name for your workspace."
      />

      <form onSubmit={handleSubmit}>
        <ModalBody>
          <FormField
            label="Workspace name"
            error={errors.name}
          >
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="Enter workspace name"
              disabled={isSubmitting}
              autoFocus
              leftIcon={<Edit className={getFormIconClassName(!!errors.name)} />}
            />
          </FormField>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            loadingText="Renaming..."
          >
            Rename workspace
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}